import { useState } from "react";
import { Button } from "./ui/button";
import { FileText, Download, ChevronDown, Check, HeartPulse } from "lucide-react";
import { downloadCsv, downloadPdf } from "../lib/download";

const PRESETS = [
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "180d", label: "Last 6 months" },
  { key: "365d", label: "Last year" },
  { key: "all", label: "All time" },
];

const PRESET_LOOKUP = Object.fromEntries(PRESETS.map((p) => [p.key, p.label]));

/**
 * Compact export control that lets a coach pick a date range then fire PDF/CSV
 * downloads. Optional `healthReportEndpoint` adds a third "Full health report"
 * button so the same picker drives combined exports.
 */
export default function ExportRangePicker({
  pdfEndpoint,
  csvEndpoint,
  pdfFilename,
  csvFilename,
  healthReportEndpoint = null,
  healthReportFilename = "amino-exchange-health-report.pdf",
  testidPrefix = "export",
  label = "Export",
}) {
  const [range, setRange] = useState("all");
  const [pickerOpen, setPickerOpen] = useState(false);

  const paramString = range && range !== "all" ? `?range=${range}` : "";
  const selectedLabel = PRESET_LOOKUP[range] || "All time";

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`${testidPrefix}-controls`}>
      {/* Range button */}
      <div className="relative">
        <Button
          type="button"
          variant="outline"
          onClick={() => setPickerOpen((v) => !v)}
          onBlur={() => setTimeout(() => setPickerOpen(false), 150)}
          className="border-slate-200 bg-white hover:bg-slate-50 text-slate-800 rounded-none h-10 px-3 uppercase tracking-widest text-xs font-semibold"
          data-testid={`${testidPrefix}-range-toggle`}
          title="Filter export to a date range"
        >
          {selectedLabel}
          <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
        </Button>
        {pickerOpen && (
          <div
            className="absolute right-0 mt-1 min-w-[180px] bg-white border border-slate-200 rounded-md shadow-lg z-30 py-1"
            data-testid={`${testidPrefix}-range-menu`}
          >
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onMouseDown={(ev) => { ev.preventDefault(); setRange(p.key); setPickerOpen(false); }}
                className={`w-full flex items-center justify-between text-left px-3 py-1.5 text-sm hover:bg-slate-50 ${range === p.key ? "text-teal-700 font-semibold" : "text-slate-700"}`}
                data-testid={`${testidPrefix}-range-${p.key}`}
              >
                <span>{p.label}</span>
                {range === p.key && <Check className="w-3.5 h-3.5 text-teal-600" />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* PDF */}
      <Button
        onClick={() => downloadPdf(pdfEndpoint + paramString, pdfFilename)}
        className="bg-[#0D9488] hover:bg-[#0F766E] text-white rounded-none h-10 px-4 uppercase tracking-widest text-xs font-semibold"
        data-testid={`${testidPrefix}-pdf`}
        title={`${label} PDF for ${selectedLabel.toLowerCase()}`}
      >
        <FileText className="w-3.5 h-3.5 mr-2" /> {label} PDF
      </Button>

      {/* CSV */}
      <Button
        variant="outline"
        onClick={() => downloadCsv(csvEndpoint + paramString, csvFilename)}
        className="border-slate-200 bg-white hover:bg-slate-50 text-slate-800 rounded-none h-10 px-4 uppercase tracking-widest text-xs font-semibold"
        data-testid={`${testidPrefix}-csv`}
        title={`Raw CSV for ${selectedLabel.toLowerCase()}`}
      >
        <Download className="w-3.5 h-3.5 mr-2" /> CSV
      </Button>

      {/* Optional combined health report */}
      {healthReportEndpoint && (
        <Button
          variant="outline"
          onClick={() => downloadPdf(healthReportEndpoint + paramString, healthReportFilename)}
          className="border-teal-300 bg-teal-50 hover:bg-teal-100 text-teal-800 rounded-none h-10 px-4 uppercase tracking-widest text-xs font-semibold"
          data-testid={`${testidPrefix}-health-report`}
          title={`Full clinician report (shots + nutrition + weight) for ${selectedLabel.toLowerCase()}`}
        >
          <HeartPulse className="w-3.5 h-3.5 mr-2" /> Full Health Report
        </Button>
      )}
    </div>
  );
}
